import { computed, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue'
import { useQuery } from '@pinia/colada'
import {
  searchQboCustomersPaged,
  searchQboInvoicesPaged,
  searchQboItemsPaged,
  searchQboPaymentsPaged,
  type QboCustomerSearchResult,
  type QboInvoiceSearchResult,
  type QboItemSearchResult,
  type QboPaymentSearchResult,
} from '@/api/lytAccountingClient'
import { useAuthStore } from '@/stores/authStore'

/** Entity types the association dialog can search in QBO. */
export type EntityAssociationDialogEntityType = 'item' | 'customer' | 'invoice' | 'payment'

/** Union of QBO search rows returned for dialog autocomplete. */
export type EntityAssociationDialogQboSearchResult =
  | QboItemSearchResult
  | QboCustomerSearchResult
  | QboInvoiceSearchResult
  | QboPaymentSearchResult

export type EntityAssociationDialogQboSearchPage = {
  items: EntityAssociationDialogQboSearchResult[]
  totalCount: number
  page: number
  size: number
}

export type EntityAssociationDialogQboSearchQueryParams = {
  entityType: MaybeRefOrGetter<EntityAssociationDialogEntityType>
  /** Page size for autocomplete; defaults to 10 to match prior dialog UX. */
  size?: MaybeRefOrGetter<number>
}

export type EntityAssociationDialogQboSearchKeyParts = {
  entityType: EntityAssociationDialogEntityType
  query: string
  page: number
  size: number
  integrationId: number | null
  storeId: string | null
}

/** Prefix shared by every dialog QBO autocomplete cache entry. */
export const ENTITY_ASSOCIATION_DIALOG_QBO_SEARCH_ROOT = [
  'entity-association-dialog-qbo-search',
] as const

/**
 * Stable, non-secret cache identity for dialog QBO autocomplete reads.
 * Includes entity type, free-text query, page/size, and auth context —
 * never API keys or tokens.
 */
export function entityAssociationDialogQboSearchQueryKey(
  parts: EntityAssociationDialogQboSearchKeyParts,
): readonly [
  'entity-association-dialog-qbo-search',
  EntityAssociationDialogEntityType,
  string,
  number,
  number,
  number | null,
  string | null,
] {
  return [
    'entity-association-dialog-qbo-search',
    parts.entityType,
    parts.query,
    parts.page,
    parts.size,
    parts.integrationId,
    parts.storeId,
  ] as const
}

export type EntityAssociationDialogQboSearchQuery = {
  items: Ref<EntityAssociationDialogQboSearchResult[]>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  status: Ref<string>
  /**
   * Explicit autocomplete entrypoint. Enables the gated Colada query,
   * awaits settlement, and returns the page items (or rethrows).
   */
  search: (query: string) => Promise<EntityAssociationDialogQboSearchResult[]>
  refetch: () => Promise<unknown>
}

/**
 * Dialog-scoped Pinia Colada read boundary for Lytning-origin QBO autocomplete.
 * Uses typed paged search clients only; callers must not own a parallel
 * search lifecycle against legacy limit-based QBO search paths.
 */
export function useEntityAssociationDialogQboSearchQuery(
  params: EntityAssociationDialogQboSearchQueryParams,
): EntityAssociationDialogQboSearchQuery {
  const auth = useAuthStore()

  const searchQuery = ref('')
  const page = ref(1)
  // Gate: no network until AsyncAutocomplete explicitly searches.
  const searchEnabled = ref(false)

  const size = computed(() => {
    const raw = params.size === undefined ? 10 : toValue(params.size)
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 10
  })

  const storeId = computed(
    () => auth.selectedStore?.storeId || auth.storeID || null,
  )

  const {
    data,
    error,
    isLoading,
    status,
    refetch,
  } = useQuery<EntityAssociationDialogQboSearchPage>({
    key: () =>
      entityAssociationDialogQboSearchQueryKey({
        entityType: toValue(params.entityType),
        query: searchQuery.value,
        page: page.value,
        size: size.value,
        integrationId: auth.currentIntegration?.integrationId ?? null,
        storeId: storeId.value,
      }),
    enabled: () => searchEnabled.value,
    // User-typed autocomplete; avoid background refetches mutating the list.
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    query: async () => {
      const entityType = toValue(params.entityType)
      const q = searchQuery.value
      const p = page.value
      const s = size.value
      if (entityType === 'item') {
        return searchQboItemsPaged(q, p, s)
      }
      if (entityType === 'customer') {
        return searchQboCustomersPaged(q, p, s)
      }
      if (entityType === 'invoice') {
        return searchQboInvoicesPaged(q, p, s)
      }
      return searchQboPaymentsPaged(q, p, s)
    },
  })

  const items = computed<EntityAssociationDialogQboSearchResult[]>(() =>
    error.value ? [] : (data.value?.items ?? []),
  )

  async function search(
    query: string,
  ): Promise<EntityAssociationDialogQboSearchResult[]> {
    searchQuery.value = query
    page.value = 1
    searchEnabled.value = true

    const state = await refetch()
    if (state?.error) {
      throw state.error instanceof Error
        ? state.error
        : new Error(String(state.error))
    }
    return state?.data?.items ?? data.value?.items ?? []
  }

  return {
    items,
    isLoading,
    error,
    status,
    search,
    refetch,
  }
}
